import { Button, ButtonSize } from '@lemon8/design';
import { Link } from '@remix-run/react';
import { FC, useCallback, useMemo } from 'react';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useAtomValue } from 'jotai';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { useSemanticUrlBuilder } from '~/utils/use-semantic-url-builder';
import { useTranslation } from 'react-i18next';
import { UserAuthIcon } from '~/components/user-auth-icon';
import { Avatar } from '~/components/avatar';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';

import './style.scss';

export const GalleryAuthorSection: FC = () => {
  const buildSemanticUrl = useSemanticUrlBuilder();
  const { t } = useTranslation();
  const article = useAtomValue($CurrentArticleAsAvailable);
  const { author } = article;
  const activeToCurrentArticle = useActiveToCurrentArticle();

  const mainAuthorRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.main_content,
        current_position: ClickPosition.main_author,
      });
    }, []),
  });

  const followBtnObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.main_content,
        current_position: ClickPosition.main_author_follow_btn,
      });
    }, []),
  });

  const authorHref = useMemo(() => {
    const { linkName } = author;
    return buildSemanticUrl(`/${linkName}`, article.articleRegion);
  }, [author]);

  const handleAuthorInfoClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.main_author);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_author,
        page_click_id: author.userId,
      }),
    },
    [activeToCurrentArticle, author],
  );

  const handleFollowClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.main_author_follow_btn);
      },
      defaultBehavior: () => {
        activeToCurrentArticle(ClickPosition.main_author_follow_btn);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_author_follow_btn,
        page_click_id: author.userId,
      }),
    },
    [activeToCurrentArticle, author],
  );

  return (
    <section id="article-author">
      <div className="article-author" ref={mainAuthorRef}>
        <Link className="article-author-avatar" to={authorHref} onClick={handleAuthorInfoClick}>
          <Avatar src={author.avatar} alt={author.nickName} />
        </Link>
        <Link className="article-author-name" to={authorHref} onClick={handleAuthorInfoClick}>
          <span>{author.nickName}</span>
          <UserAuthIcon authInfo={author.authInfo} width={16} height={16} />
        </Link>
        <div className="article-author-follow" ref={followBtnObservedRef}>
          <Button variant="Secondary" size={ButtonSize.small} title={t('follow')} onClick={handleFollowClick} />
        </div>
      </div>
    </section>
  );
};
